import * as React from "react"
import { cn } from "cn"
import { PortraitCard } from "./portrait-card"

type ConversationShellProps = React.ComponentProps<"section"> & {
  portraitLayout?: boolean
  companions?: React.ReactNode
  avatar: React.ReactNode
  surfaceOpen: boolean
  surface?: React.ReactNode
  identity?: React.ReactNode
  status?: React.ReactNode
  controls?: React.ReactNode
  avatarRef?: React.Ref<HTMLDivElement>
  avatarClassName?: string
  conversationClassName?: string
  surfaceClassName?: string
}

/**
 * Presentation only: keep the session/renderer owner above this shell, pass the
 * same avatar component type and key, and never conditionally replace it when
 * opening work. Observe avatarRef to resize a canvas without recreating it.
 *
 * Work stays mounted while hidden. The conversation footer reserves the entire
 * avatar corner, including on mobile; work fields/actions never sit beneath it.
 * Controls and status remain outside the clipped portrait. Consumers own focus
 * handoff, permission checks, voice state, draft lifetime, and operation state.
 */
function ConversationShell({
  portraitLayout = false,
  companions,
  avatar,
  surfaceOpen,
  surface,
  identity,
  status,
  controls,
  avatarRef,
  avatarClassName,
  conversationClassName,
  surfaceClassName,
  children,
  className,
  ...props
}: ConversationShellProps) {
  if (portraitLayout) return (
    <section data-slot="conversation-shell" data-surface-open={surfaceOpen} className={cn("flex min-h-0 min-w-0 flex-col bg-background text-foreground", className)} {...props}>
      <div data-slot="conversation-surface" hidden={!surfaceOpen} className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto", surfaceClassName)}>{surface}</div>
      <div data-slot="conversation-presence" className={cn("min-h-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]", surfaceOpen ? "shrink-0 border-t border-border" : "flex flex-1 flex-col overflow-y-auto", conversationClassName)}>
        <div data-slot="portrait-grid" className={cn(
          surfaceOpen ? "w-full" : companions ? "mx-auto grid w-full max-w-5xl grid-cols-1 items-start gap-5 sm:grid-cols-2 lg:grid-cols-3" : "m-auto w-full max-w-sm"
        )}>
          <PortraitCard portrait={avatar} identity={identity} status={status} controls={controls} compact={surfaceOpen} avatarRef={avatarRef} className={avatarClassName} />
          <div hidden={surfaceOpen || !companions} className="contents [&[hidden]]:hidden">{companions}</div>
        </div>
        {children && <div data-slot="conversation-content" className="mx-auto mt-4 w-full max-w-lg shrink-0">{children}</div>}
      </div>
    </section>
  )
  return (
    <section
      data-slot="conversation-shell"
      data-surface-open={surfaceOpen}
      className={cn("flex min-h-0 min-w-0 flex-col bg-background text-foreground", className)}
      {...props}
    >
      <div
        data-slot="conversation-surface"
        hidden={!surfaceOpen}
        className={cn("min-h-0 min-w-0 flex-1", surfaceClassName)}
      >
        {surface}
      </div>
      <div
        data-slot="conversation-presence"
        className={cn(
          "relative min-w-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]",
          surfaceOpen ? "shrink-0 border-t border-border pr-32 sm:pr-36" : "flex flex-1 flex-col justify-center",
          conversationClassName
        )}
      >
        <div
          ref={avatarRef}
          data-slot="conversation-avatar"
          data-presentation={surfaceOpen ? "corner" : "portrait"}
          className={cn(
            "overflow-hidden border border-border bg-muted",
            surfaceOpen
              ? "absolute right-4 bottom-[max(1rem,env(safe-area-inset-bottom))] size-24 rounded-full sm:size-28"
              : "mx-auto aspect-[3/4] w-full max-w-sm rounded-3xl",
            avatarClassName
          )}
        >
          {avatar}
        </div>
        <div
          data-slot="conversation-controls"
          className={cn(
            "min-w-0 space-y-3",
            surfaceOpen ? "flex min-h-24 flex-col justify-center sm:min-h-28" : "mx-auto mt-5 w-full max-w-lg text-center"
          )}
        >
          {identity && <div className="text-sm font-medium">{identity}</div>}
          {status && <div role="status" className="text-sm text-muted-foreground">{status}</div>}
          {controls && <div className={cn("flex flex-wrap items-center gap-2 [&_button]:h-auto [&_button]:min-h-8 [&_button]:max-w-full [&_button]:whitespace-normal", !surfaceOpen && "justify-center")}>{controls}</div>}
        </div>
        {children && <div data-slot="conversation-content" className="mt-4 min-w-0">{children}</div>}
      </div>
    </section>
  )
}

export { ConversationShell }
export type { ConversationShellProps }
